<script setup>
import { ref } from 'vue'
import { MapLayer, MapSource, MapFeature } from '@/components/map'
import { LayerPlanetVars, LayerPlanetscope, LayerOrder, LayerRadar, LayerCollectedData } from '@/components/layers'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { DialogConfirm } from '@/components/dialogs'
import { Button } from '@/components/ui/button'
import { Trash2 } from 'lucide-vue-next'

const emit = defineEmits(['isLoaded', 'deleteLayer'])
const props = defineProps({
  map: Object,
  item: Object,
  beforeId: String,
  layerId: String,
  orgId: Number,
  isVisible: Boolean,
  isSettings: Boolean,
})

const showGeometry = ref(false)
const opacity = ref(100)

const onOpacityChange = (value) => {
  opacity.value = value[0]

  const layers = props.map.getStyle().layers.filter((layer) =>
    layer.metadata?.groupId === props.layerId
  )

  layers.forEach((layer) => {
    // change layer opacity based on type
    if (layer.type === 'fill') {
      props.map.setPaintProperty(
        layer.id,
        'fill-opacity',
        opacity.value / 100
      )
    } else if (layer.type === 'line') {
      props.map.setPaintProperty(
        layer.id,
        'line-opacity',
        opacity.value / 100
      )
    } else if (layer.type === 'circle') {
      props.map.setPaintProperty(
        layer.id,
        'circle-opacity',
        opacity.value / 100
      )
    } else if (layer.type === 'symbol') {
      props.map.setPaintProperty(
        layer.id,
        'text-opacity',
        opacity.value / 100
      )
    } else if (layer.type === 'raster') {
      props.map.setPaintProperty(
        layer.id,
        'raster-opacity',
        opacity.value / 100
      )
    }
  })
}

const deleteLayer = () => {
  emit('deleteLayer', props.layerId)
}
</script>

<template>
<div class="ml-2">
  <div>
    <!-- Parent source and layer to control z-index, don't care what inside -->
    <MapSource
      :map="map"
      type="geojson"
      :id="layerId + '-source'"
      :data="{
        type: 'FeatureCollection',
        features: []
      }"
    >
      <MapLayer
        :map="map"
        :id="layerId"
        :source="layerId + '-source'"
        type="fill"
        :before-id="beforeId"
      >
        <div v-if="isSettings" class="pb-2 pr-2 border border-gray-200 rounded-md px-2 py-1 mb-2 mr-2">
          <h3 class="text-sm font-bold mb-2">Settings</h3>
          <div class="space-y-3">
            <div>
              <h4>Opacity</h4>
              <div class="flex items-center justify-between">
                <Slider
                  :default-value="[opacity]"
                  :max="100"
                  :step="1"
                  @update:model-value="onOpacityChange($event)"
                />
                <span class="w-7 ml-1 flex">
                  {{opacity}}
                </span>
              </div>
            </div>

            <div v-if="item.geometry">
              <label class="flex items-center space-x-1.5">
                <Switch
                  :model-value="showGeometry"
                  @update:model-value="showGeometry = $event"
                />
                <span>Show Geometry</span>
              </label>
            </div>

            <div class="border-t border-gray-200 pt-2 flex justify-end">
              <DialogConfirm
                ref="dialogDeleteConfirm"
                @confirm="deleteLayer"
                title="Delete layer"
                description="Are you sure you want to delete this layer?"
              >
                <Button size="sm" variant="secondary" class="flex items-center">
                  <Trash2 class="w-4 h-4 text-red-500" />
                  <span class="ml-2 text-red-500">Delete Layer</span>
                </Button>
              </DialogConfirm>
            </div>

          </div>
        </div>

        <div>
          <MapFeature
            v-if="showGeometry && item.geometry"
            :map="map"
            :feature="{ type: 'Feature', geometry: item.geometry, properties: {}}"
            :key="layerId"
            :options="{
              isFill: false
            }"
          />

          <LayerPlanetscope
            v-if="item.properties.type === 0"
            :map="map"
            :before-id="beforeId"
            :group-id="layerId"
            :org-id="orgId"
            :layer-id="layerId"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />

          <LayerOrder
            :map="map"
            :layer="item.properties.layer"
            :layer-id="layerId"
            v-if="item.properties.type === 1"
            :before-id="beforeId"
            :group-id="layerId"
            :options="{
              minzoom: 0,
              maxzoom: 22,
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />

          <LayerPlanetVars
            v-if="item.properties.type === 2"
            :map="map"
            :layer-id="layerId"
            :code="item.properties.code"
            :layer="item.properties.layer"
            :before-id="beforeId"
            :group-id="layerId"
            :isVisible="isVisible"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />


          <LayerRadar
            v-if="item.properties.type === 3"
            :map="map"
            :layer-id="layerId"
            :code="item.uuid"
            :layer="item.properties.layer"
            :before-id="beforeId"
            :group-id="layerId"
            :is-visible="isVisible"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />



          <LayerCollectedData
            v-if="item.properties.type === 4"
            :map="map"
            :layer-id="layerId"
            :code="item.uuid"
            :layer="item.properties.layer"
            :before-id="beforeId"
            :group-id="layerId"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />
        </div>


      </MapLayer>
    </MapSource>
  </div>
</div>
</template>
